import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Upload, Trash2, Eye, Plus, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { api, formatError, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LEN = { one_day: "One-Time", one_week: "1-Week", thirty_day: "30-Days" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

const Plans = ({ plans }) => (
  <div className="space-y-3">
    {plans.length === 0 && <p className="text-sm text-slate-600" data-testid="dashboard-no-plans">No plans yet. <Link to="/questionnaire" className="text-[#0D9488] font-semibold">Build your first plan</Link>.</p>}
    {plans.map((p) => (
      <Link key={p.id} to={`/plan/${p.id}`} data-testid={`dashboard-plan-${p.id}`} className="lift flex flex-wrap items-center gap-4 bg-white border border-[#E4DFD4] rounded-xl p-5">
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold">{p.plan?.title || "Plan in progress"}</div>
          <div className="text-xs text-slate-500 mt-1">{fmt(p.created_at)} · {LEN[p.program_length]} · {p.document_count || 0} document{p.document_count === 1 ? "" : "s"}</div>
        </div>
        <span className={`text-xs font-mono uppercase px-2.5 py-1 rounded-full ${p.status === "ready" ? "bg-[#0D9488]/10 text-[#0F766E]" : p.status === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
      </Link>
    ))}
  </div>
);

const Documents = () => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);
  const upload = async (files) => {
    setBusy(true);
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post("/documents", fd); toast.success(`${f.name} uploaded`); } catch (e) { toast.error(formatError(e)); }
    }
    setBusy(false); ref.current.value = ""; load();
  };
  const view = async (doc) => {
    const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const del = async (doc) => { await api.delete(`/documents/${doc.id}`); load(); };
  return (
    <div>
      <button data-testid="dashboard-upload-btn" onClick={() => ref.current.click()} className="inline-flex items-center gap-2 rounded-full bg-[#0B1013] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0D9488] transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload lab report
      </button>
      <input ref={ref} type="file" data-testid="dashboard-upload-input" accept=".pdf,.txt,.csv" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      <p className="text-xs text-slate-500 mt-2">PDF, TXT or CSV up to 10 MB. Select documents to include when you build your next plan.</p>
      <div className="mt-5 space-y-2">
        {docs.length === 0 && <p className="text-sm text-slate-600" data-testid="dashboard-no-docs">No documents uploaded yet.</p>}
        {docs.map((d) => (
          <div key={d.id} data-testid={`dashboard-doc-${d.id}`} className="flex items-center gap-3 bg-white border border-[#E4DFD4] rounded-xl p-4">
            <FileText className="w-5 h-5 text-[#0D9488]" />
            <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{d.original_filename}</div><div className="text-xs text-slate-500">{fmt(d.created_at)} · {(d.size / 1024).toFixed(0)} KB</div></div>
            <button data-testid={`doc-view-${d.id}`} onClick={() => view(d)} className="p-2 text-slate-500 hover:text-[#0D9488]"><Eye className="w-4 h-4" /></button>
            <button data-testid={`doc-delete-${d.id}`} onClick={() => del(d)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders/mine").then((r) => setOrders(r.data)); }, []);
  return (
    <div className="space-y-3">
      {orders.length === 0 && <p className="text-sm text-slate-600" data-testid="dashboard-no-orders">No orders yet. <Link to="/shop" className="text-[#0D9488] font-semibold">Visit the shop</Link>.</p>}
      {orders.map((o) => (
        <div key={o.id} data-testid={`dashboard-order-${o.id}`} className="bg-white border border-[#E4DFD4] rounded-xl p-5">
          <div className="flex justify-between text-sm"><span className="font-mono">#{o.id.slice(0, 8).toUpperCase()} · {fmt(o.created_at)}</span><span className="font-mono font-bold">{money(o.amount_total)}</span></div>
          <div className="text-sm text-slate-600 mt-2">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</div>
          <div data-testid={`order-status-${o.id}`} className="text-xs mt-3 font-mono uppercase text-[#0F766E]">
            {o.fulfillment_status === "shipped" ? `Shipped${o.carrier ? ` via ${o.carrier}` : ""} · Tracking ${o.tracking_number}` : "Paid · preparing to ship"}
          </div>
          <Link to={`/orders/${o.id}`} data-testid={`order-details-link-${o.id}`} className="inline-block mt-3 text-sm font-semibold text-[#0D9488] hover:underline">{o.fulfillment_status === "shipped" ? "Track order →" : "Order details →"}</Link>
        </div>
      ))}
    </div>
  );
};

const Settings = () => {
  const { user, setUser } = useAuth();
  const update = async (k, v) => {
    try { const { data } = await api.patch("/auth/settings", { [k]: v }); setUser(data); toast.success("Saved"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-4 max-w-xl">
      <label className="flex items-center justify-between gap-4 bg-white border border-[#E4DFD4] rounded-xl p-5">
        <span><span className="block font-semibold">Monthly re-assessment reminders</span><span className="block text-sm text-slate-600">We'll email {user.email} 30 days after your latest plan.</span></span>
        <Switch data-testid="settings-reminders-switch" checked={!!user.reminders_enabled} onCheckedChange={(v) => update("reminders_enabled", v)} />
      </label>
      <label className="flex items-center justify-between gap-4 bg-white border border-[#E4DFD4] rounded-xl p-5">
        <span><span className="block font-semibold">Daily session reminders</span><span className="block text-sm text-slate-600">While a plan is active, we'll email you each morning with that day's codes and the buttons to press.</span></span>
        <Switch data-testid="settings-daily-reminders-switch" checked={user.daily_reminders !== false} onCheckedChange={(v) => update("daily_reminders", v)} />
      </label>
      <label className="flex items-center justify-between gap-4 bg-white border border-[#E4DFD4] rounded-xl p-5">
        <span><span className="block font-semibold">I own a Wave Therapy device</span><span className="block text-sm text-slate-600">Used to tailor plan recommendations.</span></span>
        <Switch data-testid="settings-owns-device-switch" checked={!!user.owns_device} onCheckedChange={(v) => update("owns_device", v)} />
      </label>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);
  const latest = plans.find((p) => p.status === "ready");
  const days = latest ? Math.floor((Date.now() - new Date(latest.created_at)) / 86400000) : null;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#0D9488]">My dashboard</span>
          <h1 className="font-display text-4xl font-semibold mt-2">Welcome, {user.name}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {latest && <Link to={`/questionnaire?from=${latest.id}`} data-testid="dashboard-next-month-btn" className="inline-flex items-center gap-2 rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white px-5 py-3 text-sm font-semibold transition-colors"><RefreshCw className="w-4 h-4" />Create next month's plan</Link>}
          <Link to="/questionnaire" data-testid="dashboard-new-plan-btn" className="inline-flex items-center gap-2 rounded-full border border-[#E4DFD4] bg-white px-5 py-3 text-sm font-semibold hover:border-[#0D9488] transition-colors"><Plus className="w-4 h-4" />New plan</Link>
        </div>
      </div>
      {latest && <p className="mt-4 text-sm text-slate-600" data-testid="dashboard-last-plan-age">Your latest plan is {days} day{days === 1 ? "" : "s"} old{days >= 30 ? " — time for your monthly re-assessment." : "."}</p>}
      <Tabs defaultValue="plans" className="mt-10">
        <TabsList className="bg-[#EFECE5]">
          <TabsTrigger value="plans" data-testid="dashboard-tab-plans">My plans</TabsTrigger>
          <TabsTrigger value="docs" data-testid="dashboard-tab-docs">Health documents</TabsTrigger>
          <TabsTrigger value="orders" data-testid="dashboard-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="settings" data-testid="dashboard-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="mt-6"><Plans plans={plans} /></TabsContent>
        <TabsContent value="docs" className="mt-6"><Documents /></TabsContent>
        <TabsContent value="orders" className="mt-6"><Orders /></TabsContent>
        <TabsContent value="settings" className="mt-6"><Settings /></TabsContent>
      </Tabs>
    </div>
  );
}
